import React, { useCallback } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_CURRENTLY_REGISTERED_VEHICLES,
  GetCurrentlyRegisteredVehiclesData,
  GetCurrentlyRegisteredVehiclesVars,
} from "common/graphql/queries";
import { usePermitApplicationContext } from "./PermitApplicationContext";
import { SelectVehiclesStepContent } from "./components/SelectVehiclesStepContent";

/**
 * Route component for the Select Vehicles step.
 * Handles data fetching and context management, delegating rendering to SelectVehiclesStepContent.
 */
export const SelectVehiclesStep: React.FC = () => {
  const { state, setSelectedVehicles } = usePermitApplicationContext();
  const permitType = state.permitTypeSelection.permitType;

  // Fetch user's registered vehicles (skip for per-account permits).
  // Uses fetchMore to paginate through all vehicles beyond the initial page,
  // since Hasura enforces a server-side row limit on registered_vehicle.
  const { data, loading, error, refetch, fetchMore } = useQuery<
    GetCurrentlyRegisteredVehiclesData,
    GetCurrentlyRegisteredVehiclesVars
  >(GET_CURRENTLY_REGISTERED_VEHICLES, {
    variables: { limit: 1000, offset: 0 },
    fetchPolicy: "cache-and-network",
    skip: permitType?.vehicleScope === "per_account",
    onCompleted: (data) => {
      if (data.registeredVehicles.length < data.registeredVehicleCount.aggregate.count) {
        fetchMore({
          variables: {
            offset: data.registeredVehicles.length,
          },
        });
      }
    },
  });

  const vehicles = data?.registeredVehicles || [];

  // Handler for selection changes
  const handleSelectionChange = useCallback(
    (selectedIds: string[], applyToAll: boolean, autoAssign: boolean) => {
      setSelectedVehicles(selectedIds, applyToAll, autoAssign);
    },
    [setSelectedVehicles],
  );

  // Handler for when a vehicle is added
  const handleVehicleAdded = useCallback(
    (vehicleId: string) => {
      // Auto-select newly added vehicle
      const newSelectedIds = [...state.vehicleSelection.selectedVehicleIds, vehicleId];
      setSelectedVehicles(newSelectedIds, false, state.vehicleSelection.autoAssignNewVehicles);
      refetch();
    },
    [state.vehicleSelection, setSelectedVehicles, refetch],
  );

  // Handler for retry on error
  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <SelectVehiclesStepContent
      permitType={permitType}
      vehicles={vehicles}
      loading={loading}
      error={error}
      selectedVehicleIds={[...state.vehicleSelection.selectedVehicleIds]}
      applyToAllVehicles={state.vehicleSelection.applyToAllVehicles}
      autoAssignNewVehicles={state.vehicleSelection.autoAssignNewVehicles}
      onSelectionChange={handleSelectionChange}
      onVehicleAdded={handleVehicleAdded}
      onRetry={handleRetry}
    />
  );
};

export default SelectVehiclesStep;
